import { useEffect, useMemo, useState } from 'react'
import { shallowEqual } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/query'
import { ActivityWorkflowState } from '@/api/models/aggregate'
import { ProctoringSessionState } from '@/api/models/proctoringSession'
import { useGetActivityProctoringSessionQuery } from '@/store/api/proctoringSessionApi'
import { useAppSelector } from '@/store/hooks'

export const useProctoringSessionFinished = () => {
  const { current: aggregate, objective, aggregates } = useAppSelector((store) => store.aggregate, shallowEqual)

  const [isCourseReviewDisabled, setIsCourseReviewDisabled] = useState(false)

  const userId = aggregate?.userId
  const objectiveActivityId = objective?.activity?.id
  const objectiveWorkflow = aggregate?.lastObjectiveWorkflow
  const lastActivityWorkflow = aggregates?.[0]?.lastActivityWorkflow

  const queryArgs = useMemo(() => {
    if (
      !userId ||
      !objectiveActivityId ||
      !objectiveWorkflow?.id ||
      !lastActivityWorkflow?.id ||
      !lastActivityWorkflow?.lastProctoringSessionId
    ) {
      return undefined
    }

    return {
      objectiveWorkflowId: objectiveWorkflow.id,
      activityWorkflowId: lastActivityWorkflow.id,
    }
  }, [
    userId,
    objectiveActivityId,
    objectiveWorkflow?.id,
    lastActivityWorkflow?.id,
    lastActivityWorkflow?.lastProctoringSessionId,
  ])

  const { data: proctoringSessionData, isLoading: isLoadingProctoringSession } = useGetActivityProctoringSessionQuery(
    queryArgs ?? skipToken,
  )

  useEffect(() => {
    if (
      !userId ||
      !objectiveActivityId ||
      !objectiveWorkflow?.id ||
      !lastActivityWorkflow?.id ||
      !lastActivityWorkflow?.lastProctoringSessionId
    ) {
      setIsCourseReviewDisabled(false)
      return
    }

    if (isLoadingProctoringSession) return

    if (proctoringSessionData) {
      const { state } = proctoringSessionData

      const isAllowed =
        (state !== ProctoringSessionState.FINISHED &&
          lastActivityWorkflow?.state === ActivityWorkflowState.inProgress) ||
        (state === ProctoringSessionState.FINISHED && lastActivityWorkflow.state === ActivityWorkflowState.finished)

      setIsCourseReviewDisabled(!isAllowed)
      return
    }

    setIsCourseReviewDisabled(false)
  }, [
    userId,
    objectiveActivityId,
    objectiveWorkflow?.id,
    proctoringSessionData,
    isLoadingProctoringSession,
    lastActivityWorkflow,
  ])

  return [isCourseReviewDisabled, isLoadingProctoringSession]
}
